"use client";
// app/components/tracking/PageViewTracker.tsx
// Route değiştiğinde GA4 + FB + TikTok'a page_view gönderir.
import { useEffect, useRef } from "react";
import { usePathname, useSearchParams } from "next/navigation";
import { trackGa4Event } from "@/app/lib/tracking/ga4";

/**
 * Sayfa görüntülemesi SAYILMAYAN query parametreleri.
 *
 * `v` = ürün detayındaki seçili beden. ProductInformation `handleSizeChange`
 * içinde `history.replaceState(.../${slug}?v=${size})` yazıyor; Next.js bu iki
 * history metodunu yamalayıp router state'ini güncellediği için
 * `useSearchParams()` yeni değeri döndürüyor ve bu effect yeniden çalışıyordu.
 * Sonuç: kullanıcı beden butonlarına her dokunduğunda GA4'e page_view, Meta'ya
 * PageView, TikTok'a ttq.page() gidiyordu — üç kanalda da sahte sayfa
 * görüntülemesi. Beden seçmek sayfa değiştirmek değil.
 *
 * Renk değişimi bu listede YOK ve olmamalı: o `pushState` ile pathname'i
 * (/${slug}) değiştiriyor, yani gerçekten başka bir ürün URL'i — page_view
 * atılması doğru.
 */
const IGNORED_PARAMS = ["v"];

/** Sayım anahtarı: yok sayılan parametreler çıkarılmış URL. */
const trackablePath = (
  pathname: string,
  params: URLSearchParams,
): string => {
  const filtered = new URLSearchParams(params);
  IGNORED_PARAMS.forEach((p) => filtered.delete(p));
  const qs = filtered.toString();
  return qs ? `${pathname}?${qs}` : pathname;
};

const PageViewTracker = () => {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const isFirst = useRef(true);
  // Son GÖNDERİLEN url. Effect yok sayılan bir parametre yüzünden yeniden
  // çalıştığında hesaplanan url değişmez, bu karşılaştırma da event'i keser.
  const lastTracked = useRef<string | null>(null);

  useEffect(() => {
    const url = trackablePath(pathname, searchParams);

    // İlk render: script'ler zaten ilk PageView'ı atar
    if (isFirst.current) {
      isFirst.current = false;
      lastTracked.current = url;
      return;
    }

    if (lastTracked.current === url) return;
    lastTracked.current = url;

    // GA4
    trackGa4Event("page_view", { page_path: url });

    // Facebook Pixel
    if (typeof window !== "undefined" && typeof window.fbq === "function") {
      window.fbq("track", "PageView");
    }

    // TikTok
    if (typeof window !== "undefined" && window.ttq) {
      window.ttq.page();
    }
  }, [pathname, searchParams]);

  return null;
};

export default PageViewTracker;